import { type FC, useCallback } from 'react'

import { type ProspectDto } from '@api'
import { type Row } from '@tanstack/react-table'

import RemoveIcon from '@/assets/icons/icon-delete-outline.svg'
import EditIcon from '@/assets/icons/icon-edit.svg'
import MoreIcon from '@/assets/icons/icon-menu-more.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { openDialog } from '@/models/dialogs.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'

import { useRemoveProspect } from '../../hooks/use-remove-prospect'
import { ExportProspectAudit } from '../export-prospect-audit/ExportProspectAudit'

interface Props {
  row: Row<ProspectDto>
}

export const ProspectActions: FC<Props> = ({ row }) => {
  const { removeProspect } = useRemoveProspect()

  const handleEdit = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_PROSPECT,
      activeProspect: row.original,
    })
  }, [row.original])

  const handleRemove = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CONFIRMATION,
      title: 'Remove Prospect',
      desc: `Are you sure you want to remove the prospect "${row.original.name}"?`,
      confirmButtonVariant: 'destructive',
      confirmText: 'Remove',
      onConfirm: () => removeProspect(row.original.id!),
    })
  }, [removeProspect, row.original.id, row.original.name])

  return (
    <div className="flex justify-end">
      <ExportProspectAudit row={row} />
      <DropdownMenu>
        <DropdownMenuTrigger>
          <IconButton
            icon={MoreIcon}
            iconClassName="h-6 w-6"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="p-1"
        >
          <DropdownMenuItem onClick={handleEdit}>
            <EditIcon className="h-4 w-4" />
            <span>Edit</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleRemove}>
            <RemoveIcon className="h-4 w-4" />
            <span>Delete</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
